import { Avatar, Button, Drawer, Dropdown, Input, Layout, Menu, Space, Tooltip } from 'antd';
import {
  BankOutlined,
  BellOutlined,
  MoonOutlined,
  SunOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  NotificationOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useUiStore } from '../stores/ui-store';
import { useAuthStore } from '../stores/auth-store';
import { usePermissions } from '../hooks/use-permissions';
import { useBreakpoint } from '../hooks/use-breakpoint';
import { NAV_GROUP_LABELS, NAV_ITEMS, type NavGroup } from './nav-items';
import { useInboxUnreadCount } from '../api/inbox';

const { Header, Sider, Content } = Layout;

/** Persistent chrome for authenticated routes. Redesigned per §2.4 with a
 *  glassy navy sidebar, refined pill-search header, and pulse-notification. */
export function AppShell() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const clearAuth = useAuthStore((s) => s.clear);
  const me = useAuthStore((s) => s.me);
  const { can } = usePermissions();
  const location = useLocation();
  const { data: unread } = useInboxUnreadCount();
  const unreadCount = unread?.count ?? 0;
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useBreakpoint();

  // Mobile-only: the sidebar renders as a slide-in Drawer. State is local
  // (not persisted) — on a phone, users open it briefly to tap a link.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    // Auto-close on route change so a nav tap dismisses the drawer.
    if (!isDesktop) setMobileNavOpen(false);
  }, [location.pathname, isDesktop]);

  const visibleItems = useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) =>
          item.requires.length === 0 ||
          item.requires.some((r) => can(r.module, r.action, r.scope)),
      ),
    [can],
  );

  const activeKey =
    visibleItems.find((n) =>
      n.path === '/' ? location.pathname === '/' : location.pathname.startsWith(n.path),
    )?.key ?? 'dashboard';

  const menuItems = useMemo(() => {
    const GROUP_ORDER: NavGroup[] = ['core', 'my-work', 'people-org', 'admin'];
    const result: Parameters<typeof Menu>[0]['items'] = [];

    for (const group of GROUP_ORDER) {
      const children = visibleItems
        .filter((item) => item.group === group)
        .map((item) => {
          const Icon = item.icon;
          return {
            key: item.key,
            icon: <Icon />,
            label: <NavLink to={item.path}>{item.label}</NavLink>,
          };
        });

      if (children.length === 0) continue;

      const label = NAV_GROUP_LABELS[group];
      if (label) {
        result.push({ type: 'group' as const, label, children });
      } else {
        result.push(...children);
      }
    }
    return result;
  }, [visibleItems]);

  const quickActionItems = useMemo(() => {
    type MenuItem = NonNullable<Parameters<typeof Menu>[0]['items']>[number];
    const base: MenuItem[] = [
      { key: 'clock-in', icon: <ClockCircleOutlined />, label: 'Clock In / Out' },
      { key: 'apply-leave', icon: <CalendarOutlined />, label: 'Apply for Leave' },
      { key: 'submit-expense', icon: <BankOutlined />, label: 'Submit Expense' },
      { key: 'raise-ticket', icon: <CustomerServiceOutlined />, label: 'Raise a Ticket' },
    ];
    const admin: MenuItem[] = [];
    if (can('employees', 'create', 'org'))
      admin.push({ key: 'add-employee', icon: <TeamOutlined />, label: 'Add Employee' });
    if (can('engage', 'edit', 'org'))
      admin.push({ key: 'announcement', icon: <NotificationOutlined />, label: 'New Announcement' });
    if (can('payroll', 'run', 'org'))
      admin.push({ key: 'run-payroll', icon: <CreditCardOutlined />, label: 'Run Payroll' });

    return admin.length > 0
      ? [...base, { type: 'divider' as const }, ...admin]
      : base;
  }, [can]);

  const onQuickAction = ({ key }: { key: string }) => {
    const routes: Record<string, string> = {
      'clock-in': '/attendance',
      'apply-leave': '/leave',
      'submit-expense': '/expenses/claims',
      'raise-ticket': '/helpdesk/my-tickets',
      'add-employee': '/employees',
      'announcement': '/engage/announcements',
      'run-payroll': '/payroll/runs',
    };
    const path = routes[key];
    if (path) navigate(path);
  };

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: 'My Profile' },
      { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
      { type: 'divider' as const },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Log out', danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'logout') {
        clearAuth();
        navigate('/login', { replace: true });
      } else if (key === 'profile') {
        navigate('/me');
      } else if (key === 'settings') {
        navigate('/settings');
      }
    },
  };

  const userInitial = (me?.user.displayName ?? '?').charAt(0).toUpperCase();

  // On mobile the drawer is always "expanded" (never collapsed to icon-only).
  const showCollapsed = isDesktop && collapsed;

  const sidebarBody = (
    <>
      {/* Brand block */}
      <div
        style={{
          height: 68,
          display: 'flex',
          alignItems: 'center',
          padding: showCollapsed ? '0' : '0 18px',
          justifyContent: showCollapsed ? 'center' : 'space-between',
          gap: 10,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: 1,
            minWidth: 0,
            justifyContent: showCollapsed ? 'center' : 'flex-start',
          }}
        >
          {/* Brand logo on a white pill so the coloured mark stays legible
              against the dark navy sidebar. */}
          <div
            style={{
              width: showCollapsed ? 38 : 42,
              height: 38,
              borderRadius: 10,
              background: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            <img
              src="/logo.png"
              alt="D Table"
              style={{
                maxHeight: 26,
                maxWidth: showCollapsed ? 30 : 60,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </div>
          {/* Logo alone — no wordmark next to it per brand preference. */}
        </div>

        {/* Mobile-only close button — hidden on desktop Sider */}
        {!isDesktop && (
          <Button
            type="text"
            aria-label="Close menu"
            icon={<CloseOutlined style={{ color: 'rgba(255,255,255,0.75)' }} />}
            onClick={() => setMobileNavOpen(false)}
          />
        )}
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[activeKey]}
        style={{ borderRight: 0, background: 'transparent', paddingTop: 6 }}
        items={menuItems}
        inlineIndent={16}
      />

    </>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: 'var(--db-bg)' }}>
      {isDesktop ? (
        <Sider
          className="db-sidebar"
          theme="dark"
          collapsible
          collapsed={collapsed}
          trigger={null}
          width={240}
          collapsedWidth={72}
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'auto',
            background:
              'linear-gradient(180deg, var(--db-sidebar-bg) 0%, var(--db-sidebar-bg-2) 100%)',
            borderRight: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {sidebarBody}
        </Sider>
      ) : (
        <Drawer
          className="db-sidebar"
          rootClassName="db-sidebar"
          placement="left"
          width={260}
          onClose={() => setMobileNavOpen(false)}
          open={mobileNavOpen}
          closable={false}
          styles={{
            body: {
              padding: 0,
              background:
                'linear-gradient(180deg, var(--db-sidebar-bg) 0%, var(--db-sidebar-bg-2) 100%)',
              overflow: 'auto',
            },
            header: { display: 'none' },
            mask: { backdropFilter: 'blur(2px)' },
          }}
        >
          {sidebarBody}
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 4 : 12,
            padding: isMobile ? '0 10px' : '0 20px',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid var(--db-border-soft)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            height: 64,
          }}
        >
          <Button
            type="text"
            icon={
              isDesktop
                ? collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                : <MenuOutlined />
            }
            onClick={() => (isDesktop ? toggle() : setMobileNavOpen(true))}
            aria-label="Toggle sidebar"
            style={{ color: 'var(--db-text-secondary)' }}
          />

          {isMobile ? (
            <Button
              type="text"
              icon={<SearchOutlined />}
              aria-label="Search"
              style={{ color: 'var(--db-text-secondary)' }}
            />
          ) : (
            <div className="db-header-search" style={{ flex: 1, maxWidth: 480 }}>
              <Input
                allowClear
                placeholder="Search employees, docs, or actions…"
                prefix={
                  <SearchOutlined style={{ color: 'var(--db-text-muted)', marginRight: 4 }} />
                }
                suffix={<span className="db-kbd">⌘K</span>}
                style={{ height: 40 }}
              />
            </div>
          )}

          <div style={{ flex: 1 }} />

          <Space size={4}>
            <Dropdown
              menu={{ items: quickActionItems, onClick: onQuickAction }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Tooltip title="Quick actions">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Quick actions"
                  style={{
                    background: 'var(--db-grad-primary)',
                    border: 'none',
                    boxShadow: '0 4px 10px rgba(2,64,139,0.22)',
                    fontWeight: 500,
                    height: isMobile ? 36 : 38,
                    borderRadius: 10,
                    padding: isMobile ? '0 10px' : '0 14px',
                  }}
                >
                  {!isMobile && 'New'}
                </Button>
              </Tooltip>
            </Dropdown>

            <Tooltip title="Inbox">
              <div style={{ position: 'relative', marginLeft: 6 }}>
                <Button
                  type="text"
                  shape="circle"
                  icon={<BellOutlined style={{ fontSize: 17 }} />}
                  aria-label="Notifications"
                  onClick={() => navigate('/inbox')}
                  style={{ color: 'var(--db-text-secondary)', width: 40, height: 40 }}
                />
                {unreadCount > 0 && (
                  <span
                    className="db-pulse"
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      minWidth: 16,
                      height: 16,
                      padding: '0 4px',
                      borderRadius: 999,
                      background: 'var(--db-danger)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 0 2px #fff',
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
            </Tooltip>

            <ThemeToggleButton />

            {!isMobile && (
              <Tooltip title="Settings">
                <Button
                  type="text"
                  shape="circle"
                  icon={<SettingOutlined style={{ fontSize: 16 }} />}
                  aria-label="Settings"
                  onClick={() => navigate('/settings')}
                  style={{ color: 'var(--db-text-secondary)', width: 40, height: 40 }}
                />
              </Tooltip>
            )}

            {!isMobile && (
              <div
                style={{
                  width: 1,
                  height: 26,
                  background: 'var(--db-border)',
                  margin: '0 6px',
                }}
              />
            )}

            <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '4px 10px 4px 4px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  transition: 'background var(--db-t-fast) var(--db-easing)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = 'var(--db-bg-soft)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Avatar
                    size={32}
                    src={me?.user.avatarUrl ?? undefined}
                    style={{ background: 'var(--db-grad-violet)', fontWeight: 600 }}
                  >
                    {userInitial}
                  </Avatar>
                  <span
                    style={{
                      position: 'absolute',
                      bottom: -1,
                      right: -1,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#4ADE80',
                      border: '2px solid #fff',
                    }}
                  />
                </div>
                {!isMobile && (
                  <div style={{ lineHeight: 1.2 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: 'var(--db-text-primary)',
                      }}
                    >
                      {me?.user.displayName?.split(' ')[0] ?? ''}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--db-text-muted)' }}>Online</div>
                  </div>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            padding: isMobile ? 12 : 28,
            background: 'var(--db-bg)',
            minHeight: 0,
          }}
        >
          <div className="db-fade-up">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

/**
 * Circular button in the top bar that flips the whole app between light and
 * dark. Icon reflects the mode you'll switch TO (moon when in light mode,
 * sun when in dark mode), matching how most editors and OS toggles behave.
 */
function ThemeToggleButton() {
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const nextMode = theme === 'dark' ? 'light' : 'dark';
  return (
    <Tooltip title={`Switch to ${nextMode} mode`}>
      <Button
        type="text"
        shape="circle"
        icon={
          theme === 'dark' ? (
            <SunOutlined style={{ fontSize: 16 }} />
          ) : (
            <MoonOutlined style={{ fontSize: 16 }} />
          )
        }
        aria-label={`Switch to ${nextMode} mode`}
        onClick={toggleTheme}
        style={{ color: 'var(--db-text-secondary)', width: 40, height: 40 }}
      />
    </Tooltip>
  );
}
